import React, { useState } from 'react';
import Header from './Header'
import Footer from './Footer'
import service1 from "../assets/service1.png";
import service2 from "../assets/service2.jpg";
import service3 from "../assets/service3.jpg";
import service4 from "../assets/service4.png";
import service5 from "../assets/service5.png";

// --- CSS Styles (Embedded for Single-File Mandate) ---
const styles = `
    /* Root Variables & Global Reset */
    :root {
        --sky-blue: #1E90FF;
        --deep-blue: #0047AB;
        --sky-orange: #FF4500;
        --light-orange: #FF8C00;
        --bg-white: #FFFFFF;
        --shadow-light: rgba(0, 71, 171, 0.15);
        --shadow-heavy: rgba(30, 144, 255, 0.4);
    }

    body {
        margin: 0;
        padding: 0;
        font-family: 'Inter', sans-serif;
        background-color: var(--bg-white);
        overflow-x: hidden; 
    }

    /* Keyframe Animations */
    @keyframes sparkle {
        0%, 100% { text-shadow: 0 0 5px var(--sky-orange), 0 0 10px var(--sky-orange), 0 0 20px var(--light-orange); }
        50% { text-shadow: none; }
    }

    @keyframes slide-in {
        from { transform: translateY(50px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
    }

    /* === Services Component Styling === */
    .services-container {
        padding: 6rem 1rem;
        background-color: var(--bg-white);
        text-align: center;
        min-height: 100vh;
    }

    .header-bar {
        background-color: var(--deep-blue); 
        color: var(--bg-white); 
        padding: 1.5rem 1rem; 
        text-align: center;
        font-size: 2rem;
        font-weight: 700;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        margin-bottom: 4rem;
    }

    .services-title {
        font-size: 3rem;
        font-weight: 800;
        color: var(--deep-blue);
        margin-bottom: 0.5rem;
        animation: slide-in 1s ease-out;
    }

    .sparkle-text {
        color: var(--sky-orange);
        animation: sparkle 2s infinite ease-in-out;
    }

    .services-grid {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 2rem;
        margin: 3rem 0;
    }

    .service-card {
        width: 100%;
        max-width: 350px;
        padding: 2rem;
        background-color: #f7faff; /* Light Blue BG */
        border-radius: 16px;
        box-shadow: 0 10px 30px var(--shadow-light);
        transition: transform 0.4s cubic-bezier(.17,.67,.83,.67), box-shadow 0.4s;
        text-align: left;
        border-top: 5px solid var(--sky-blue);
        animation: slide-in 1.2s ease-out;
    }

    .service-card:hover {
        transform: translateY(-10px) rotateX(2deg) scale(1.03); /* Tilt and lift effect */
        box-shadow: 0 20px 40px var(--shadow-heavy);
        border-top-color: var(--sky-orange);
    }

    .service-card h3 {
        color: var(--deep-blue);
        font-size: 1.5rem;
        margin-bottom: 0.75rem;
    }
    .service-card p {
        color: #555;
        line-height: 1.6;
    }

    /* Gallery */
    .gallery-section {
        margin-top: 4rem;
    }
    .gallery-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 1rem;
        margin-top: 2rem;
    }
    .gallery-item {
        overflow: hidden;
        border-radius: 12px;
        box-shadow: 0 5px 15px var(--shadow-light);
    }
    .gallery-item img {
        width: 100%;
        height: 200px;
        object-fit: cover;
        transition: transform 0.6s ease-in-out;
        display: block;
    }
    .gallery-item img:hover {
        transform: scale(1.1);
    }

    /* Inquiry Form */
    .inquiry-form-card {
        max-width: 600px;
        margin: 4rem auto 0;
        padding: 2.5rem;
        border-radius: 20px;
        background-color: var(--deep-blue);
        box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
        animation: slide-in 1.5s ease-out;
    }
    .inquiry-form-card h3 {
        color: var(--bg-white);
        margin-bottom: 1.5rem;
    }
    .inquiry-form-card input,
    .inquiry-form-card textarea {
        width: calc(100% - 20px);
        padding: 10px;
        margin-bottom: 1rem;
        border: none;
        border-radius: 8px;
        font-size: 1rem;
        outline: none;
    }
    .inquiry-form-card textarea {
        resize: vertical;
        min-height: 100px;
    }

    /* WhatsApp Button with Shine/Hover Effect */
    .whatsapp-button {
        display: inline-block;
        padding: 12px 30px;
        font-size: 1.1rem;
        font-weight: 600;
        color: var(--bg-white);
        background-color: var(--sky-orange);
        border: none;
        border-radius: 50px;
        cursor: pointer;
        transition: all 0.3s ease, box-shadow 0.6s ease-in-out;
        position: relative;
        overflow: hidden;
        z-index: 1;
        margin-top: 1rem;
    }
    .whatsapp-button:hover {
        background-color: var(--light-orange);
        box-shadow: 0 0 15px var(--sky-orange);
        transform: translateY(-2px);
    }
    .whatsapp-button::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(120deg, transparent, rgba(255,255,255,0.4), transparent);
        transition: all 0.6s;
        z-index: 2;
    }
    .whatsapp-button:hover::before {
        left: 100%;
    }

    @media (max-width: 768px) {
        .services-title {
            font-size: 2.5rem;
        }
    }
`;

// Utility component for the sparkling effect on buttons/text
const SparkleText = ({ children, color = 'var(--sky-orange)' }) => (
  <span className="sparkle-text" style={{ color }}>
    {children}
  </span>
);

// Mock Gallery Images (Placeholder URLs)
const galleryImages = [
  'https://placehold.co/400x200/1E90FF/FFFFFF?text=Installation+Expert',
  'https://placehold.co/400x200/0047AB/FFFFFF?text=Coil+Cleaning',
  'https://placehold.co/400x200/FF4500/FFFFFF?text=AC+Deep+Service',
  'https://placehold.co/400x200/FF8C00/FFFFFF?text=Unit+Repair',
];

// Services Component
const ServicesContent = () => {
  // Form state for WhatsApp inquiry
  const [formData, setFormData] = useState({ name: '', phone: '', service: '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleWhatsAppSubmit = () => {
    if (!formData.name || !formData.phone || !formData.service) {
      console.error('Validation Error: Please fill in all fields before submitting.');
      return;
    }

    const message = `Hello SkyBreeze AC Solutions, I would like to inquire about a service.\nName: ${formData.name}\nPhone: ${formData.phone}\nService Required: ${formData.service}`;
    const whatsappUrl = `https://wa.me/1234567890?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
  };
  


  const serviceData = [
    { title: 'Commercial & Residential AC Installation', description: 'World-class AC setup for homes, offices, and commercial buildings across Coimbatore and India.' },
    { title: 'Authorized Service Partner – Samsung & Carrier', description: 'Five years of consistent professional service that meets global brand standards.' },
    { title: 'AMC & Breakdown Services', description: 'Servicing high-profile clients like Taj Hotels with structured annual and emergency maintenance.' },
    { title: 'Duct Manufacturing & Supply', description: 'In-house factory producing durable, high-efficiency ducting for major commercial projects.' },
    { title: 'Support Services for Builders & Contractors', description: 'Technical manpower support, third‑party execution, detailed reporting, and project coordination.' },
    { title: 'HVAC Duct Installation & Modification', description: 'We specialize in duct installation, modification, and retrofitting, including straight ducts, bends, offsets, tapers, collars, and acoustic insulation, ensuring proper airflow balance and noise control.' },
  ];

  return (
    <section className="services-container">
        
        <h1 className="services-title">
            Premium <SparkleText>AC Services</SparkleText>
        </h1>

        {/* Service Cards Grid with Slide-In and Tilt/Hover Effect */}
        <div className="services-grid">
            {serviceData.map((service, index) => (
                <div key={index} className="service-card">
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                </div>
            ))}
        </div>

        {/* Gallery Section */}
        <div className="gallery-section">
            <h2>Service <SparkleText>Gallery</SparkleText></h2>
            <div className="gallery-grid">
                {galleryImages.map((src, index) => (
                    <div key={index} className="gallery-item">
                        <img 
                            src={src} 
                            alt={`Service Work ${index + 1}`} 
                            onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/400x200/CCCCCC/000000?text=Service+View"; }}
                        />
                    </div>
                ))}
            </div>
        </div>

        {/* WhatsApp Inquiry Form */}
        {/* <div className="inquiry-form-card">
            <h3>Quick <SparkleText>Inquiry</SparkleText> (WhatsApp Direct)</h3>
            <input
                type="text"
                name="name"
                placeholder="Your Full Name"
                value={formData.name}
                onChange={handleInputChange}
                required
            />
            <input
                type="tel"
                name="phone"
                placeholder="Your Phone Number"
                value={formData.phone}
                onChange={handleInputChange}
                required
            />
            <textarea
                name="service"
                placeholder="Describe the service you need..."
                value={formData.service}
                onChange={handleInputChange}
                required
            />
            <button
                className="whatsapp-button"
                onClick={handleWhatsAppSubmit}
            >
                Submit & Chat on WhatsApp
            </button>
        </div> */}
    </section>
  );
};

// Main Export Component
export default function Services() {
    return (
        <>
            {/* Inject the CSS styles for runnable file */}
            <style>{styles}</style>
            <Header/>
            
            {/* Header Placeholder */}
            <div className="header-bar">
                SkyBreeze AC Solutions - Our Services
            </div>
            
            <ServicesContent />
            
            {/* Footer Placeholder */}
            <Footer/>
        </>
    );
}